import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, ChevronRight } from 'lucide-react'
import { useSearchMunicipalities } from '../hooks/queries'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { formatNumber } from '../lib/utils'

export default function SearchPage() {
  const [query, setQuery] = useState('')
  const [searchTerm, setSearchTerm] = useState('') // Actual search term, updated on submit
  const navigate = useNavigate()

  // React Query handles loading, error, and caching automatically
  const { data, isLoading, error } = useSearchMunicipalities(searchTerm, 50, 0)

  const municipalities = data?.results ?? []
  const total = data?.total ?? 0
  const referenceDate = data?.reference_date

  const handleSearch = () => {
    if (query.trim().length >= 2 || query.trim() === '') {
      setSearchTerm(query)
    }
  }

  return (
    <div className="page-container-dark min-h-screen flex flex-col">
      <div className="content-wrapper flex-1 flex flex-col">
        {/* Header */}
        <Header
          subtitle={`Lagebeschreibungen für ${formatNumber(total)} deutsche Gemeinden${referenceDate ? ` (Stand: ${referenceDate})` : ''}`}
        />

        {/* Search Section */}
        <div className="card-gut8 mb-8">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Gemeinde suchen (Name, AGS oder ARS)"
                className="input-gut8 pl-12"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Suche...' : 'Suchen'}
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="status-error mb-6">
            <p>{error instanceof Error ? error.message : 'Fehler beim Laden der Gemeinden'}</p>
          </div>
        )}

        {/* Results */}
        <div className="space-y-4 flex-1">
          {isLoading && municipalities.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-text-inverse/70">Lade Gemeinden...</p>
            </div>
          ) : municipalities.length > 0 ? (
            <>
              <p className="text-text-inverse/70 mb-4">
                {total} Gemeinde{total !== 1 ? 'n' : ''} gefunden
                {searchTerm && ` für "${searchTerm}"`}
              </p>
              {municipalities.map((municipality) => (
                <div
                  key={municipality.id}
                  onClick={() => navigate(`/municipality/${municipality.municipality_key}`)}
                  className="result-card"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gut8-anthracite mb-2">
                        {municipality.name}
                      </h3>
                      <div className="space-y-1 text-sm text-text-muted">
                        <div className="flex gap-4">
                          <p>
                            <span className="font-medium">AGS:</span>{' '}
                            <span className="info-key">{municipality.ags}</span>
                          </p>
                          <p>
                            <span className="font-medium">ARS:</span>{' '}
                            <span className="info-key">{municipality.ars}</span>
                          </p>
                        </div>
                        {municipality.county && (
                          <p>
                            <span className="font-medium">Kreis:</span>{' '}
                            {municipality.county.name}
                          </p>
                        )}
                        {municipality.state && (
                          <p>
                            <span className="font-medium">Bundesland:</span>{' '}
                            {municipality.state.name}
                          </p>
                        )}
                        {municipality.population && (
                          <p>
                            <span className="font-medium">Einwohner:</span>{' '}
                            {formatNumber(municipality.population)}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="ml-4">
                      <ChevronRight className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : (
            !isLoading && (
              <div className="card-gut8 text-center py-12">
                <p className="text-text-muted">
                  {searchTerm
                    ? 'Keine Gemeinden gefunden. Versuchen Sie eine andere Suche.'
                    : 'Geben Sie einen Suchbegriff ein, um Gemeinden zu finden.'}
                </p>
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  )
}
